<template>
  <form @submit.prevent="saveDocument" class="relative">
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex flex-col items-center justify-center space-y-4 z-20"
    >
      <Spinner :color="'text-black'" :size="10" />
      <div class="mb-5 w-full" v-if="isUploading">
        <ProgressBar :progress="uploadProgress" />
      </div>
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">
        Document Number
        <span class="text-red-500">*</span>
      </label>
      <input
          v-model="editingDoc.document_number"
          required
          class="bge-input bge-input-spacing w-full rounded"
      />
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">
        Name
        <span class="text-red-500">*</span>
      </label>
      <input
          v-model="editingDoc.name"
          required
          class="bge-input bge-input-spacing w-full rounded"
      />
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">
        Document Type
        <span class="text-red-500">*</span>
      </label>
      <div class="w-full relative">
        <select
            class="bge-input bge-select rounded"
            v-model="editingDoc.document_type"
            required
        >
          <option value="download">Download</option>
          <option value="bulletin">Bulletin</option>
          <option value="general">General</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
    </div>

    <div class="w-full mb-2" v-if="editingDoc.document_type === 'bulletin'">
      <label class="uppercase font-semibold text-sm">
        Document Date
        <span class="text-red-500">*</span>
      </label>
      <Calendar
          class="w-full"
          v-model="docDateInput"
          dateFormat="dd/mm/yy"
          :showIcon="true"
      />
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">
        Visibility
        <span class="text-red-500">*</span>
      </label>
      <div class="w-full relative">
        <select
            class="bge-input bge-select rounded"
            v-model="editingDoc.visibility"
            required
        >
          <option>Internal</option>
          <option>Agent</option>
          <option>External</option>
          <option>Distributor</option>
          <option>Public</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">
        Access Level
      </label>
      <div v-if="isAccessLevelEditable" class="w-full relative">
        <select
            class="bge-input bge-select rounded"
            v-model="editingDoc.access_level"
            required
        >
          <option value="L2">Level 2 – Distributor Admins &amp; Engineers</option>
          <option value="L3">Level 3 – Distributor Admins only</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
      <div v-else class="bge-input bge-input-spacing w-full rounded bg-gray-100 text-gray-600">
        {{ accessLevelDisplay }}
      </div>
    </div>

    <div class="w-full mb-2">
      <label class="uppercase font-semibold text-sm">Category</label>
      <div class="w-full relative">
        <select class="bge-input bge-select rounded" v-model="editingDoc.category">
          <option value="">No Category</option>
          <option>Brochures</option>
          <option>Certifications</option>
          <option>Help &amp; Training</option>
          <option>Miscellaneous</option>
          <option>Product Manual</option>
          <option>Sample Plans</option>
          <option>Schematics</option>
          <option>Software</option>
          <option>Specifications</option>
        </select>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
    </div>

    <FileUpload v-if="formType === 'New'" :file.sync="editingDoc.file" />

    <div class="relative flex justify-end mt-5">
      <div v-show="!canSubmitForm" class="opacity-50 bg-white absolute inset-0"></div>

      <button
          :disabled="isBusy || !canSubmitForm"
          type="submit"
          class="flex items-center bg-blue-200 hover:bg-blue-300 text-secondary py-1 px-3 rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
          <path d="M17 21v-8H7v8M7 3v5h8" />
        </svg>
        <span>Save</span>
      </button>
    </div>
  </form>
</template>

<script>
const Spinner = () => import("@/components/Spinner.vue");
const Notification = () => import("@/components/Notification.vue");
const Toggle = () => import("@/components/Toggle.vue");
const FileUpload = () => import("@/components/FileUpload.vue");
const ProgressBar = () => import("@/components/ProgressBar.vue");
const RelatedProductsSearchField = () =>
    import("@/components/Documents/RelatedProductsSearchField.vue");

import Calendar from "primevue/calendar";

const ACCESS_LEVEL_MAP = {
  Internal: "L4",
  External: "L1",
  Public: "L1",
  Agent: "L2",
  Distributor: "L2",
};

const ACCESS_LEVEL_LABELS = {
  L1: "Level 1 – Public",
  L2: "Level 2 – Distributor Admins & Engineers",
  L3: "Level 3 – Distributor Admins only",
  L4: "Level 4 – Boston Internal Only",
};

export default {
  name: "UploadPublishedDoc",
  components: {
    Spinner,
    Notification,
    Toggle,
    FileUpload,
    ProgressBar,
    RelatedProductsSearchField,
    Calendar,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editingDoc: {},
      formType: "New",
      isBusy: false,
      isUploading: false,
      uploadProgress: 0,
      contactName: "",
      contactSearchTerm: "",
      contactResults: [],
      contactSelectOpen: false,
      customersEquipment: [],
      relatedProducts: [],
    };
  },
  async mounted() {
    if (this.document && this.document.document_id) {
      this.formType = "Edit";
      try {
        this.isBusy = true;

        this.editingDoc = await this.DocumentService.getDocumentById(
            this.document.document_id
        );

        if (!this.editingDoc.visibility) {
          this.editingDoc.visibility = "Internal";
        }

        if (!this.editingDoc.document_type) {
          this.editingDoc.document_type = "download";
        } else {
          const t = ("" + this.editingDoc.document_type).toLowerCase();
          if (t === "download" || t === "bulletin" || t === "general") {
            this.editingDoc.document_type = t;
          }
        }

        if (!this.editingDoc.access_level) {
          this.editingDoc.access_level = ACCESS_LEVEL_MAP[this.editingDoc.visibility] || "L4";
        }

        if (
            this.editingDoc.document_type === "bulletin" &&
            !this.editingDoc.document_date &&
            this.editingDoc.created_at
        ) {
          this.editingDoc.document_date = this.$moment
              .unix(this.editingDoc.created_at)
              .startOf("day")
              .unix();
        }

        this.relatedProducts = this.editingDoc.resource_tags || [];
        this.isBusy = false;
      } catch (error) {
        this.isBusy = false;
        this.$emit("complete");
      }
    } else {
      this.editingDoc = {
        visibility: "Internal",
        document_type: "download",
        access_level: "L4",
        document_date: null,
        category: "",
      };
    }
  },
  watch: {
    "editingDoc.visibility": function (newVisibility) {
      if (!this.editingDoc || !newVisibility) return;

      const v = newVisibility;
      if (v === "Agent" || v === "Distributor") {
        if (this.editingDoc.access_level !== "L2" && this.editingDoc.access_level !== "L3") {
          this.editingDoc.access_level = "L2";
        }
      } else {
        this.editingDoc.access_level = ACCESS_LEVEL_MAP[v] || "L4";
      }
    },

    "editingDoc.document_type": function (newType) {
      if (!this.editingDoc) return;

      const t = ("" + (newType || "")).toLowerCase();
      if (t !== "download" && t !== "bulletin" && t !== "general") {
        this.editingDoc.document_type = "download";
        return;
      }

      if (t === "general") {
        this.editingDoc.visibility = "Internal";
      }

      if (t === "bulletin") {
        this.editingDoc.visibility = "External";
        if (!this.editingDoc.document_date) {
          const base = this.editingDoc.created_at
              ? this.$moment.unix(this.editingDoc.created_at)
              : this.$moment();
          this.editingDoc.document_date = base.startOf("day").unix();
        }
      }

      if (t === "download") {
        this.editingDoc.visibility = "Distributor";
      }
    },
  },
  computed: {
    isAccessLevelEditable() {
      const v = this.editingDoc ? this.editingDoc.visibility : "";
      return v === "Agent" || v === "Distributor";
    },

    accessLevelDisplay() {
      const level = this.editingDoc ? this.editingDoc.access_level : "";
      return ACCESS_LEVEL_LABELS[level] || level;
    },

    docDateInput: {
      get() {
        const v = this.editingDoc ? this.editingDoc.document_date : null;
        if (!v) return null;
        return this.$moment.unix(v).toDate();
      },
      set(value) {
        if (!this.editingDoc) return;
        if (!value) {
          this.editingDoc.document_date = null;
          return;
        }
        this.editingDoc.document_date = this.$moment(value).startOf("day").unix();
      },
    },

    canSubmitForm() {
      if (!this.editingDoc) return false;

      const hasBase = !!(
          this.editingDoc.document_number &&
          this.editingDoc.name &&
          this.editingDoc.document_type &&
          this.editingDoc.visibility &&
          this.editingDoc.access_level
      );

      const type = this.editingDoc.document_type;
      const needsDocDate = type === "bulletin";
      const hasDocDate = !needsDocDate || !!this.editingDoc.document_date;

      if (this.formType === "Edit") {
        return !!(hasBase && hasDocDate);
      }

      return !!(hasBase && hasDocDate && this.editingDoc.file);
    },
  },
  methods: {
    async saveDocument() {
      if (this.editingDoc && this.editingDoc.document_id) {
        this.isBusy = true;
        try {
          const type = this.editingDoc.document_type;

          const payload = {
            document_number: this.editingDoc.document_number,
            name: this.editingDoc.name,
            category: this.editingDoc.category,
            visibility: this.editingDoc.visibility,
            document_type: type,
            document_date: type === "bulletin" ? this.editingDoc.document_date : null,
            access_level: this.editingDoc.access_level,
            description: this.editingDoc.description,
            tags: this.editingDoc.tags,
            metadata: this.editingDoc.metadata,
            document_role: this.editingDoc.document_role,
          };

          await this.DocumentService.updateDocument(this.editingDoc.document_id, payload);

          const items = this.relatedProducts || [];
          for (let i = 0; i < items.length; i++) {
            const p = items[i] || {};
            const resourceType = p.resource_type ? p.resource_type : "product";
            const resourceId = p.resource_id ? p.resource_id : p.product_id;

            if (resourceId) {
              await this.DocumentService.attachResource(this.editingDoc.document_id, {
                resource_type: resourceType,
                resource_id: resourceId,
              });
            }
          }

          this.$breadstick.notify(
              ({ h, onClose }) => {
                return h(
                    Notification,
                    {
                      props: {
                        title: "Document Saved",
                        close: onClose,
                      },
                    },
                    "Your document was saved successfully"
                );
              },
              { position: "top-right" }
          );

          this.isBusy = false;
          this.$emit("complete");
        } catch (error) {
          this.isBusy = false;
          this.$breadstick.notify(
              ({ h, onClose }) => {
                return h(
                    Notification,
                    {
                      props: {
                        type: "danger",
                        title: "Save Error",
                        close: onClose,
                      },
                    },
                    "There was a problem saving your document, please try again later"
                );
              },
              { position: "top-right" }
          );
        }
        return;
      }

      this.isBusy = true;
      try {
        const type = this.editingDoc.document_type;

        const uploadResult = await this.DocumentService.uploadDocument({
          visibility: this.editingDoc.visibility,
          document_number: this.editingDoc.document_number,
          name: this.editingDoc.name,
          category: this.editingDoc.category,
          document_type: type,
          document_date: type === "bulletin" ? this.editingDoc.document_date : null,
          access_level: this.editingDoc.access_level,
          content_type: this.editingDoc.content_type,
          content_length: this.editingDoc.content_length,
          description: this.editingDoc.description,
          tags: this.editingDoc.tags,
          metadata: this.editingDoc.metadata,
          document_role: this.editingDoc.document_role,
          file: this.editingDoc.file,
        });

        const documentId = uploadResult.document_id;

        const items = this.relatedProducts || [];
        for (let i = 0; i < items.length; i++) {
          const p = items[i] || {};
          const resourceType = p.resource_type ? p.resource_type : "product";
          const resourceId = p.resource_id ? p.resource_id : p.product_id;

          if (resourceId) {
            await this.DocumentService.attachResource(documentId, {
              resource_type: resourceType,
              resource_id: resourceId,
            });
          }
        }

        const accessUrl = uploadResult.access_url;
        const url = new URL(accessUrl);
        const accountUrl = url.origin;
        const sasToken = url.search;

        const pathParts = url.pathname.split("/").filter((p) => p && p.trim() !== "");
        const container = pathParts.length > 0 ? pathParts[0] : "";
        const nonContainerParts = this.$_.drop(pathParts, 1);
        const filename = nonContainerParts.join("/");

        const blobService = AzureStorage.Blob.createBlobServiceWithSas(accountUrl, sasToken);
        const customBlockSize =
            this.editingDoc.file[0].size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 128;
        blobService.singleBlobPutThresholdInBytes = customBlockSize;

        this.isUploading = true;

        const uploadSummary = blobService.createBlockBlobFromBrowserFile(
            container,
            filename,
            this.editingDoc.file[0],
            { blockSize: customBlockSize },
            async (error) => {
              if (error) {
                this.isUploading = false;
                this.isBusy = false;

                this.$breadstick.notify(
                    ({ h, onClose }) => {
                      return h(
                          Notification,
                          {
                            props: {
                              type: "danger",
                              title: "Upload Error",
                              close: onClose,
                            },
                          },
                          "There was a problem uploading your document, please try again later"
                      );
                    },
                    { position: "top-right" }
                );
              } else {
                await this.DocumentService.updateDocument(documentId, {
                  is_uploaded: true,
                });

                this.$breadstick.notify(
                    ({ h, onClose }) => {
                      return h(
                          Notification,
                          {
                            props: {
                              title: "Document Uploaded",
                              close: onClose,
                            },
                          },
                          "Your document was uploaded successfully"
                      );
                    },
                    { position: "top-right" }
                );

                this.isUploading = false;
                this.isBusy = false;
                this.$emit("complete");
              }
              this.uploadProgress = 0;
            }
        );

        uploadSummary.on("progress", () => {
          this.uploadProgress = parseFloat(uploadSummary.getCompletePercent());
        });
      } catch (error) {
        this.isBusy = false;
        this.isUploading = false;
        this.uploadProgress = 0;
        console.log(error);
      }
    },
  },
};
</script>
